<template>
  <UikitPageComponent
    class="osk-home-banner-page"
    :error="!homePageData && error"
    :pending="!homePageData && pending"
    @retry="getNextPageData(0)"
  >
    <HomeGenderPage
      v-if="homePageData"
      :data="homePageData"
      :loading="pending"
      :home-banner-id="(homeBannerId as string)"
      :show-concierge-modal="isConciergePage"
      @get-next-page="getNextPageData"
    />

    <BusinessSeoLinks
      v-if="seoBlockLinks"
      :links="seoBlockLinks"
    />
  </UikitPageComponent>

  <ConciergeDialog
    v-if="isConciergePage"
    v-model="isConciergeDialogOpen"
  />
</template>

<script lang="ts" setup>
import { useApi } from '~/restAPI';
import { Home } from '~/restAPI/Home';
import { CountryIds } from '~/types/constants';
import { BannerDTO, SeoLinkGroupDto } from '~/restAPI/data-contracts';

import { AnalyticsEvents } from '~/types/analytics';

const { $addEvent } = useNuxtApp();
const { createApiInstance } = useApi();
const homeApi = createApiInstance(Home);
const schemaOrg = useSchemaOrg();

const { currentCountry } = useInternational();
const { params: { homeBannerId } } = useRoute();
const {
  morePagesCount,
  moreCurrentPage,
} = useMorePagesData();
const homePageData = useHomePageData('home-banner');
const { isConciergePage, isConciergeDialogOpen } = useConcierge();

const route = useRoute();

const nextPage = ref(0);

const event = useRequestEvent();

const {
  error,
  pending,
  refresh: getPageData,
} = useLazyAsyncData(() => homeApi.getBannerContent({
  id: homeBannerId as string,
  pageNumber: nextPage.value,
  countryId: currentCountry.value?.id,
  productFiltrationEnabled: true,
  withStories: true,
  blockListVersion: 2,
  withSEO: true,
}).then((res) => {
  if (process.server) {
    const combinedCookie = res.headers.get('set-cookie');

    if (combinedCookie) {
      event.node.res.setHeader('set-cookie', combinedCookie);
    }
  }

  const resData = res.data?.data;

  morePagesCount.value = resData?.pageCount ?? 0;
  moreCurrentPage.value = resData?.pageNumber ?? 0;

  if (!homePageData.value) {
    homePageData.value = resData;
  } else if (homePageData.value?.items?.length) {
    homePageData.value.items.push(...(resData?.items ?? []));
  }

  isConciergePage.value = resData?.concierge;

  return resData;
}));

const singleBannerContent = computed(() => homePageData.value?.items?.find((item) => item.contentType === 'SINGLE_BANNER')?.content as BannerDTO);

const seoBlockLinks = computed(() => homePageData.value?.seoLinks?.contentList as SeoLinkGroupDto[]);

function getNextPageData(page: number) {
  nextPage.value = page;
  getPageData();
}

const headComp = computed(() => ({
  title: 'Дизайнерская одежда, обувь и аксессуары от бутиков и частных продавцов – Интернет-магазин Oskelly',
  bodyAttrs: {
    class: 'oskelly-2_0',
  },
  meta: [
    {
      name: 'description',
      content: 'Интернет-магазин брендовой одежды, обуви и аксессуаров для женщин от бутиков и частных продавцов - Oskelly. Женская дизайнерская одежда со скидками до 90%. Продавайте быстро и безопасно. Подарочная упаковка и доставка по Москве и всей России.',
    },
    {
      property: 'og:title',
      content: 'Дизайнерская одежда, обувь и аксессуары от бутиков и частных продавцов – Интернет-магазин Oskelly',
    },
    {
      property: 'og:url',
      content: route.path,
    },
    {
      property: 'og:image',
      content: singleBannerContent.value?.imgPath,
    },
    {
      name: 'twitter:image',
      content: singleBannerContent.value?.imgPath,
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      children: schemaOrg,
    },
  ],
}));

useHead(headComp);

onMounted(() => {
  $addEvent(AnalyticsEvents.PageView);
});

onUnmounted(() => {
  homePageData.value = undefined;
  morePagesCount.value = 0;
  moreCurrentPage.value = 0;
  isConciergePage.value = false;
});
</script>

<style lang="scss" scoped>
@import "~/assets/scss/settings/index";
</style>
